import { Form, useNavigation } from "react-router-dom";
import { useMemo, useState } from "react";
import { motion as Motion } from "framer-motion";
import i18n from "@/i18n";
import cls from "./LanguageSelect.module.scss";

import MrM from "@/assets/UI/lan/MrM.webp";
import MrsM from "@/assets/UI/lan/MrsM.webp";
import MrD from "@/assets/UI/lan/MrD.webp";
import MrsD from "@/assets/UI/lan/MrsD.webp";
import lanIcon from "@/assets/UI/lan.webp";

const LANGS = [
  { code: "en", label: "English", flag: "🇬🇧", hint: "EN" },
  { code: "si", label: "සිංහල", flag: "🇱🇰", hint: "සි" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳", hint: "हि" },
  { code: "ta", label: "தமிழ்", flag: "🇱🇰", hint: "த" },
];

const bgFloatA = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.5, 0.68, 0.5],
    scale: [1.03, 1.08, 1.03],
    x: [0, 8, 0],
    transition: { duration: 13, ease: "easeInOut", repeat: Infinity },
  },
};

const bgFloatB = {
  hidden: { opacity: 0.78 },
  show: {
    opacity: [0.78, 0.62, 0.78],
    scale: [1.08, 1.03, 1.08],
    x: [0, -10, 0],
    transition: { duration: 15, ease: "easeInOut", repeat: Infinity },
  },
};

export default function LanguageSelect() {
  const nav = useNavigation();
  const submitting = nav.state === "submitting";

  const initial = useMemo(() => {
    try {
      return localStorage.getItem("cm_lang") || "en";
    } catch {
      return "en";
    }
  }, []);

  const [current, setCurrent] = useState(initial);

  const selected = LANGS.find((l) => l.code === current) || LANGS[0];

  const onPick = (code) => {
    setCurrent(code);
    i18n.changeLanguage(code);
  };

  return (
    <main className={cls.page}>
      <section className={cls.heroStage} aria-label="Language selection">
        <div className={cls.universeFx} aria-hidden="true">
          <span className={cls.orbitOne} />
          <span className={cls.orbitTwo} />
          <span className={cls.energyLine} />
          <span className={cls.particles} />
        </div>

        <div className={cls.mobileOnly} aria-hidden="true">
          <Motion.img
            className={`${cls.heroImg} ${cls.z0}`}
            src={MrM}
            alt=""
            variants={bgFloatA}
            initial="hidden"
            animate="show"
            draggable="false"
          />

          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrsM}
            alt=""
            variants={bgFloatB}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly} aria-hidden="true">
          <Motion.img
            className={`${cls.heroImg} ${cls.z0}`}
            src={MrD}
            alt=""
            variants={bgFloatA}
            initial="hidden"
            animate="show"
            draggable="false"
          />

          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrsD}
            alt=""
            variants={bgFloatB}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.darkVeil} aria-hidden="true" />

        <Form method="post" className={cls.formOverlay}>
          <input type="hidden" name="lang" value={current} />

          <Motion.div
            className={cls.formCard}
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            <div className={cls.formTop}>
              <span className={cls.formBadge}>
                <img src={lanIcon} alt="" className={cls.formBadgeIcon} />
              </span>

              <div className={cls.formMeta}>
                <div className={cls.formTitle}>Choose your language</div>
                <div className={cls.formSub}>
                  Tap a tile. You can change it later.
                </div>
              </div>
            </div>

            <div className={cls.langGrid} role="radiogroup">
              {LANGS.map((l) => {
                const active = l.code === current;

                return (
                  <button
                    key={l.code}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => onPick(l.code)}
                    className={`${cls.langTile} ${
                      active ? cls.langTileActive : ""
                    }`}
                  >
                    <span className={cls.langFlag}>{l.flag}</span>
                    <span className={cls.langLabel}>{l.label}</span>
                    <span className={cls.langHint}>{l.hint}</span>
                  </button>
                );
              })}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className={`${cls.ctaBtn} ${submitting ? cls.ctaBtnBusy : ""}`}
            >
              {submitting ? "Saving…" : `Continue with ${selected.label}`}
            </button>
          </Motion.div>
        </Form>
      </section>
    </main>
  );
}
